import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, type RowSelectionState, type VisibilityState } from '@tanstack/react-table';
import { AlertOctagon, Archive, ArchiveRestore, ArrowDown, ArrowUp, Ban, Bot, CalendarDays, ChevronDown, ChevronRight, CircleDashed, Columns3, LayoutList, PencilLine, Plus, Search, SlidersHorizontal, TableProperties, X } from 'lucide-react';
import { WORK_TYPES, type UpdateWorkItem, type WorkItemListItem } from '@vwt/contracts';
import { isActivePhase, isClosedPhase } from '@vwt/domain';
import {
  Badge,
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogFooter,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Field,
  Input,
  NativeSelect,
  Textarea,
  toast,
} from '@vwt/ui';
import { api, newIdempotencyKey, POLL_ACTIVE, queryClient, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';
import { InfiniteScrollSentinel } from '../../components/infinite-scroll.js';
import { hasUnsavedChanges } from '../../lib/unsaved.js';
import { AgentCreatedIcon, DeliveryBadge, DueLabel, KeyLabel, NoEvidenceBadge, PhaseBadge, PointsBadge, PriorityLabel, ProgressPill } from '../../components/badges.js';
import { formatCalendarDate } from '../../lib/format.js';
import { DateWithTime, PageHeader, PrincipalPicker, QueryState, TimeAgo, UserChip } from '../../components/common.js';
import { SavedViewsMenu } from '../../components/saved-views.js';
import { ExportMenu } from '../../components/export-menu.js';
import { DateInput } from '../../components/date-input.js';
import { LabelBadge, LabelManagerDialog, useLabelCatalog } from '../../components/labels.js';
import { useMe } from '../../lib/session.js';
import { effectiveDir, listParam, nextSort, toggleListValue } from '../../lib/list-sort.js';
import { FilterChips, PeopleFilter, ResultCount, SortHeader } from '../../components/table-tools.js';
import { useFilterChips } from './work-filter-chips.js';
import { FieldEditor, FieldManagerDialog, FieldValue, isSelectField, useCustomFields } from '../../components/custom-fields.js';
import { reachablePhases, usePhaseMove } from './phase-move.js';
import { WorkCalendar } from './work-calendar.js';
import { CreateWorkDialog } from './create-work.js';
import { WorkDetail } from './work-detail.js';
import { WorkBoard } from './work-board.js';
import { GROUP_FIELDS, groupItems, isGroupField, isLaneField, LANE_FIELDS, type GroupField, type LaneField } from './grouping.js';

const FILTER_KEYS = ['q', 'phase', 'type', 'priority', 'assigneeId', 'ownerId', 'createdById', 'projectId', 'teamId', 'blocked', 'associationStatus', 'includeCanceled', 'archived', 'unverified', 'agentCreated', 'due', 'label', 'field', 'sort', 'dir'] as const;
/** URL parameters that only arrange the list; they are kept when filters are cleared. */
const LAYOUT_KEYS = ['view', 'sort', 'dir', 'group', 'lanes', 'closed'] as const;
/** How far back done and canceled work is listed; the board would otherwise page through years of history. */
const CLOSED_WINDOWS = ['7', '30', '90', 'all'] as const;
const DEFAULT_CLOSED_WINDOW = '30';
const PEOPLE_KEYS = ['assigneeId', 'ownerId', 'createdById'];
/** Column id → list sort. */
const SORTABLE: Record<string, string> = { key: 'key', title: 'title', phase: 'phase', priority: 'priority', type: 'type', assignee: 'assignee', scope: 'project', startDate: 'startDate', dueDate: 'dueDate', createdAt: 'createdAt', updatedAt: 'updatedAt' };

export function WorkListPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'board' ? 'board' : params.get('view') === 'calendar' ? 'calendar' : 'list';
  const selected = params.get('selected');
  const [search, setSearch] = useState(params.get('q') ?? '');
  const filters = useMemo(() => Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k) ?? undefined]).filter(([, v]) => v)) as Record<string, string>, [params]);
  const closedParam = params.get('closed');
  const closed = CLOSED_WINDOWS.find((w) => w === closedParam) ?? DEFAULT_CLOSED_WINDOW;
  // The archived view is all finished work, so the window does not apply there.
  const listFilters = useMemo(() => (closed === 'all' || filters.archived === 'true' ? filters : { ...filters, closedWithinDays: closed }), [filters, closed]);

  const setParam = (key: string, value: string | null) => {
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: key === 'q' },
    );
  };

  // Debounced search keeps typing responsive and the URL shareable (no protected content in URLs).
  useEffect(() => {
    // Only write when the text changed: an unconditional write on mount would replace newer params (e.g. create=1).
    if ((params.get('q') ?? '') === search.trim()) return;
    const id = setTimeout(() => setParam('q', search.trim() || null), 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const list = useInfiniteQuery({
    queryKey: ['listWorkItems', 'infinite', listFilters, view],
    queryFn: ({ pageParam, signal }) => api.call('listWorkItems', { query: { ...listFilters, limit: view === 'board' ? '100' : '50', ...(pageParam ? { cursor: pageParam } : {}) }, signal }),
    // The calendar loads its own month window.
    enabled: view !== 'calendar',
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last?.meta.nextCursor ?? undefined,
    refetchInterval: POLL_ACTIVE,
  });
  const items = useMemo(() => list.data?.pages.flatMap((p) => p?.data ?? []) ?? [], [list.data]);

  const createOpen = params.get('create') === '1';
  const [columnVisibility, setColumnVisibility] = useColumnVisibility();
  const [editing, setEditing] = useStoredFlag('vwt.work.editTable');
  const activeFilterCount = FILTER_KEYS.filter((k) => k !== 'q' && k !== 'sort' && k !== 'dir' && params.get(k)).length;
  const total = list.data?.pages[0]?.meta.total;
  const chips = useFilterChips(params, setParam);
  const sortDir = effectiveDir(params.get('sort') ?? 'updatedAt', params.get('dir'));

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={t('work.title')}
        subtitle={t('work.subtitle')}
        actions={
          <>
            <div className="inline-flex rounded-[var(--radius)] border border-border-strong p-0.5" role="group" aria-label="View">
              <Button size="sm" variant={view === 'list' ? 'secondary' : 'ghost'} className={cn(view === 'list' && 'shadow-none')} onClick={() => setParam('view', null)} aria-pressed={view === 'list'}>
                <LayoutList /> {t('work.views.list')}
              </Button>
              <Button size="sm" variant={view === 'board' ? 'secondary' : 'ghost'} className={cn(view === 'board' && 'shadow-none')} onClick={() => setParam('view', 'board')} aria-pressed={view === 'board'}>
                <Columns3 /> {t('work.views.board')}
              </Button>
              <Button size="sm" variant={view === 'calendar' ? 'secondary' : 'ghost'} className={cn(view === 'calendar' && 'shadow-none')} onClick={() => setParam('view', 'calendar')} aria-pressed={view === 'calendar'}>
                <CalendarDays /> {t('calendar.title')}
              </Button>
            </div>
            <Button variant="primary" onClick={() => setParam('create', '1')}>
              <Plus /> {t('nav.newWork')}
            </Button>
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2 pb-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-fg-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('nav.search')} className="pl-8" aria-label={t('nav.search')} />
          </div>
          <FilterMenu params={params} setParam={setParam} count={FILTER_KEYS.filter((k) => !['q', 'sort', 'dir', ...PEOPLE_KEYS].includes(k) && params.get(k)).length} />
          <PeopleFilter
            params={params}
            setParam={setParam}
            roles={[
              { param: 'assigneeId', label: t('work.columns.assignee'), noneLabel: t('common.unassigned') },
              { param: 'ownerId', label: t('work.columns.owner'), noneLabel: t('table.noOwner') },
              { param: 'createdById', label: t('table.createdBy') },
            ]}
          />
          <SavedViewsMenu
            scope="WORK"
            params={params}
            onApply={(query) => {
              setSearch(query.q ?? '');
              setParams(new URLSearchParams(query));
            }}
          />
          <ExportMenu path="/api/v1/work-items/export" filters={listFilters} />
          <QuickToggle active={listParam(params, 'assigneeId').includes('me')} onClick={() => setParam('assigneeId', toggleListValue(params, 'assigneeId', 'me'))}>
            {t('common.me')}
          </QuickToggle>
          <QuickToggle active={params.get('blocked') === 'true'} onClick={() => setParam('blocked', params.get('blocked') === 'true' ? null : 'true')}>
            <AlertOctagon className="size-3.5" /> {t('work.filters.blocked')}
          </QuickToggle>
          <QuickToggle active={params.get('agentCreated') === 'true'} onClick={() => setParam('agentCreated', params.get('agentCreated') === 'true' ? null : 'true')}>
            <Bot className="size-3.5" /> {t('agentCreated.filter')}
          </QuickToggle>
          <QuickToggle active={params.get('associationStatus') === 'PROVISIONAL'} onClick={() => setParam('associationStatus', params.get('associationStatus') === 'PROVISIONAL' ? null : 'PROVISIONAL')}>
            <CircleDashed className="size-3.5" /> {t('work.filters.provisional')}
          </QuickToggle>
          {activeFilterCount ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setParams((p) => {
                  const next = new URLSearchParams();
                  for (const key of LAYOUT_KEYS) if (p.get(key)) next.set(key, p.get(key)!);
                  return next;
                })
              }
            >
              <X /> {t('common.clearFilters')}
            </Button>
          ) : null}
          {view !== 'calendar' ? <ResultCount total={total} loaded={items.length} className="ml-auto" /> : <span className="ml-auto" />}
          {view === 'list' ? (
            <div className="hidden items-center gap-1 md:flex">
              <Button size="sm" variant={editing ? 'secondary' : 'ghost'} aria-pressed={editing} onClick={() => setEditing(!editing)} title={t('table.editMode')} data-testid="edit-table">
                <PencilLine /> <span className="hidden lg:inline">{t('table.editMode')}</span>
              </Button>
              <ColumnsMenu visibility={columnVisibility} onChange={setColumnVisibility} />
            </div>
          ) : null}
          {view === 'calendar' ? null : view === 'list' ? (
            <NativeSelect value={params.get('group') ?? ''} onChange={(e) => setParam('group', e.target.value || null)} className="w-auto" aria-label={t('work.groupBy')}>
              <option value="">{t('work.groupNone')}</option>
              {GROUP_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {t('work.groupBy')}: {t(`work.groupFields.${f}`)}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <NativeSelect value={params.get('lanes') ?? ''} onChange={(e) => setParam('lanes', e.target.value || null)} className="w-auto" aria-label={t('work.lanes')}>
              <option value="">{t('work.lanesNone')}</option>
              {LANE_FIELDS.map((f) => (
                <option key={f} value={f}>
                  {t('work.lanes')}: {t(`work.groupFields.${f}`)}
                </option>
              ))}
            </NativeSelect>
          )}
          {view === 'calendar' || params.get('archived') === 'true' ? null : (
            <NativeSelect value={closed} onChange={(e) => setParam('closed', e.target.value === DEFAULT_CLOSED_WINDOW ? null : e.target.value)} className="w-auto" aria-label={t('work.closedWindow.label')}>
              {CLOSED_WINDOWS.map((w) => (
                <option key={w} value={w}>
                  {w === 'all' ? t('work.closedWindow.all') : t('work.closedWindow.days', { count: Number(w) })}
                </option>
              ))}
            </NativeSelect>
          )}
          {view === 'calendar' ? null : (
          <>
          <NativeSelect
            value={params.get('sort') ?? 'updatedAt'}
            onChange={(e) =>
              setParams((p) => {
                const next = new URLSearchParams(p);
                if (e.target.value === 'updatedAt') next.delete('sort');
                else next.set('sort', e.target.value);
                next.delete('dir');
                return next;
              })
            }
            className="w-auto"
            aria-label={t('common.sort')}
          >
            <option value="updatedAt">{t('work.columns.updated')}</option>
            <option value="priority">{t('work.columns.priority')}</option>
            <option value="createdAt">{t('common.created')}</option>
            <option value="dueDate">{t('work.columns.due')}</option>
            <option value="startDate">{t('work.columns.start')}</option>
            <option value="title">{t('work.columns.title')}</option>
            <option value="phase">{t('work.columns.phase')}</option>
            <option value="assignee">{t('work.columns.assignee')}</option>
            <option value="project">{t('work.columns.project')}</option>
            <option value="key">{t('work.columns.key')}</option>
          </NativeSelect>
          <Button
            size="iconSm"
            variant="ghost"
            onClick={() => {
              const sort = params.get('sort') ?? 'updatedAt';
              setParam('dir', nextSort(sort, params.get('dir'), sort).dir);
            }}
            aria-label={sortDir === 'asc' ? t('table.ascending') : t('table.descending')}
            title={sortDir === 'asc' ? t('table.ascending') : t('table.descending')}
          >
            {sortDir === 'asc' ? <ArrowUp /> : <ArrowDown />}
          </Button>
          </>
          )}
        </div>
        <FilterChips chips={chips} />
      </PageHeader>
      <div className="min-h-0 flex-1 overflow-auto">
        {view === 'calendar' ? (
          <WorkCalendar filters={filters} month={params.get('month')} onMonth={(month) => setParam('month', month)} onOpen={(id) => setParam('selected', id)} />
        ) : (
        <QueryState query={list} empty={items.length === 0 ? <EmptyState icon={<LayoutList />} title={t('work.empty')} action={<Button onClick={() => setParam('create', '1')}><Plus /> {t('nav.newWork')}</Button>} /> : undefined}>
          {view === 'board' ? <WorkBoard items={items} onOpen={(id) => setParam('selected', id)} lanes={isLaneField(params.get('lanes')) ? (params.get('lanes') as LaneField) : null} /> : <WorkTable items={items} selectedId={selected} onOpen={(id) => setParam('selected', id)} columnVisibility={columnVisibility} groupBy={isGroupField(params.get('group')) ? (params.get('group') as GroupField) : null} params={params} setParams={setParams} editing={editing} />}
          <InfiniteScrollSentinel query={list} />
        </QueryState>
        )}
      </div>
      <Drawer open={!!selected} onOpenChange={(open) => {
          if (open) return;
          if (hasUnsavedChanges()) {
            toast.warning(t('common.unsavedChanges'));
            return;
          }
          setParam('selected', null);
        }}
        onEscapeKeyDown={(event) => {
          if (!hasUnsavedChanges()) return;
          event.preventDefault();
          toast.warning(t('common.unsavedChanges'));
        }} title={t('work.title')} closeLabel={t('common.close')}>
        {selected ? <WorkDetail id={selected} inDrawer /> : null}
      </Drawer>
      <CreateWorkDialog open={createOpen} onOpenChange={(open) => setParam('create', open ? '1' : null)} />
    </div>
  );
}

function QuickToggle({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} className={cn('inline-flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-medium', active ? 'border-accent bg-accent-soft text-accent-soft-fg' : 'border-border-strong text-fg-muted hover:bg-muted')}>
      {children}
    </button>
  );
}

function FilterMenu({ params, setParam, count }: { params: URLSearchParams; setParam: (key: string, value: string | null) => void; count: number }) {
  const { t } = useTranslation();
  const me = useMe();
  const catalog = useLabelCatalog();
  const [managing, setManaging] = useState(false);
  const [managingFields, setManagingFields] = useState(false);
  // Choice and yes/no fields can filter the list (one field at a time).
  const filterFields = useCustomFields().filter((f) => !f.archived && (isSelectField(f) || f.type === 'BOOLEAN'));
  const used = useApiQuery('listWorkLabels', { query: {} });
  const labelOptions = [...new Set([...catalog.keys(), ...(used.data?.data ?? []).slice(0, 15).map((l) => l.label)])].slice(0, 25);
  const projects = useApiQuery('listProjects', { query: {} });
  const teams = useApiQuery('listTeams', { query: {} });
  const toggleInList = (key: string, value: string) => setParam(key, toggleListValue(params, key, value));
  const has = (key: string, value: string) => listParam(params, key).includes(value);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="md" variant="secondary">
          <SlidersHorizontal /> {t('common.filter')}
          {count ? <Badge tone="accent">{count}</Badge> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="max-h-[70vh] w-64 overflow-y-auto">
        <DropdownMenuLabel>{t('work.filters.phase')}</DropdownMenuLabel>
        {(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'INTEGRATED', 'DONE', 'CANCELED', 'EXPIRED'] as const).map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggleInList('phase', p))}>
            <Checkbox checked={has('phase', p)} aria-hidden tabIndex={-1} /> {t(`status.phase.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.priority')}</DropdownMenuLabel>
        {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggleInList('priority', p))}>
            <Checkbox checked={has('priority', p)} aria-hidden tabIndex={-1} /> {t(`status.priority.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.type')}</DropdownMenuLabel>
        {WORK_TYPES.map((p) => (
          <DropdownMenuItem key={p} onSelect={(e) => (e.preventDefault(), toggleInList('type', p))}>
            <Checkbox checked={has('type', p)} aria-hidden tabIndex={-1} /> {t(`status.workType.${p}`)}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.due')}</DropdownMenuLabel>
        {(
          [
            ['overdue', t('work.filters.dueOverdue')],
            ['soon', t('work.filters.dueSoon')],
            ['none', t('work.filters.dueNone')],
          ] as const
        ).map(([value, label]) => (
          <DropdownMenuItem key={value} onSelect={(e) => (e.preventDefault(), setParam('due', params.get('due') === value ? null : value))}>
            <Checkbox checked={params.get('due') === value} aria-hidden tabIndex={-1} /> {label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.project')}</DropdownMenuLabel>
        {(projects.data?.data ?? []).map((p) => (
          <DropdownMenuItem key={p.id} onSelect={(e) => (e.preventDefault(), toggleInList('projectId', p.id))}>
            <Checkbox checked={has('projectId', p.id)} aria-hidden tabIndex={-1} /> {p.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('work.filters.team')}</DropdownMenuLabel>
        {(teams.data?.data ?? []).map((team) => (
          <DropdownMenuItem key={team.id} onSelect={(e) => (e.preventDefault(), toggleInList('teamId', team.id))}>
            <Checkbox checked={has('teamId', team.id)} aria-hidden tabIndex={-1} /> {team.name}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => (e.preventDefault(), setParam('includeCanceled', params.get('includeCanceled') === 'true' ? null : 'true'))}>
          <Checkbox checked={params.get('includeCanceled') === 'true'} aria-hidden tabIndex={-1} /> {t('work.filters.includeCanceled')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={(e) => (e.preventDefault(), setParam('unverified', params.get('unverified') === 'true' ? null : 'true'))}>
          <Checkbox checked={params.get('unverified') === 'true'} aria-hidden tabIndex={-1} /> {t('noEvidence.filter')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>{t('agentCreated.origin')}</DropdownMenuLabel>
        {(['true', 'false'] as const).map((value) => (
          <DropdownMenuItem key={value} onSelect={(e) => (e.preventDefault(), setParam('agentCreated', params.get('agentCreated') === value ? null : value))}>
            <Checkbox checked={params.get('agentCreated') === value} aria-hidden tabIndex={-1} /> {t(value === 'true' ? 'agentCreated.filter' : 'agentCreated.filterPeople')}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => (e.preventDefault(), setParam('archived', params.get('archived') === 'true' ? null : 'true'))}>
          <Checkbox checked={params.get('archived') === 'true'} aria-hidden tabIndex={-1} /> {t('work.filters.archived')}
        </DropdownMenuItem>
        {labelOptions.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('work.filters.label')}</DropdownMenuLabel>
            {labelOptions.map((l) => (
              <DropdownMenuItem key={l} onSelect={(e) => (e.preventDefault(), toggleInList('label', l))}>
                <Checkbox checked={has('label', l)} aria-hidden tabIndex={-1} /> <LabelBadge label={l} catalog={catalog} />
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        {filterFields.map((f) => (
          <Fragment key={f.id}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{f.name}</DropdownMenuLabel>
            {(f.type === 'BOOLEAN' ? [{ id: 'true', label: t('common.yes') }, { id: 'false', label: t('common.no') }] : f.options).map((o) => (
              <DropdownMenuItem key={o.id} onSelect={(e) => (e.preventDefault(), setParam('field', params.get('field') === `${f.id}:${o.id}` ? null : `${f.id}:${o.id}`))}>
                <Checkbox checked={params.get('field') === `${f.id}:${o.id}`} aria-hidden tabIndex={-1} /> {o.label}
              </DropdownMenuItem>
            ))}
            <DropdownMenuItem onSelect={(e) => (e.preventDefault(), setParam('field', params.get('field') === `${f.id}:` ? null : `${f.id}:`))}>
              <Checkbox checked={params.get('field') === `${f.id}:`} aria-hidden tabIndex={-1} /> {t('table.empty')}
            </DropdownMenuItem>
          </Fragment>
        ))}
        {me.permissions.canManageLabels || me.permissions.canManageFields ? <DropdownMenuSeparator /> : null}
        {me.permissions.canManageLabels ? <DropdownMenuItem onSelect={() => setManaging(true)}>{t('labels.manage')}</DropdownMenuItem> : null}
        {me.permissions.canManageFields ? <DropdownMenuItem onSelect={() => setManagingFields(true)}>{t('fields.manage')}</DropdownMenuItem> : null}
      </DropdownMenuContent>
      {managing ? <LabelManagerDialog onClose={() => setManaging(false)} suggestions={(used.data?.data ?? []).map((l) => l.label)} /> : null}
      {managingFields ? <FieldManagerDialog onClose={() => setManagingFields(false)} /> : null}
    </DropdownMenu>
  );
}

const col = createColumnHelper<WorkItemListItem>();

/** Columns people can hide or show; the choice is a per-browser preference. */
const OPTIONAL_COLUMNS = ['phase', 'priority', 'type', 'points', 'assignee', 'owner', 'scope', 'team', 'labels', 'startDate', 'dueDate', 'delivery', 'createdAt', 'updatedAt'] as const;
const DEFAULT_HIDDEN: VisibilityState = { type: false, owner: false, team: false, labels: false, startDate: false };
const COLUMNS_STORAGE_KEY = 'vwt.work.columns.v2';
/** Before v2 the created column was hidden by default and saved that way; v2 drops that saved choice once. */
const LEGACY_COLUMNS_STORAGE_KEY = 'vwt.work.columns';

function loadColumnVisibility(): VisibilityState {
  try {
    let stored = JSON.parse(localStorage.getItem(COLUMNS_STORAGE_KEY) ?? 'null') as VisibilityState | null;
    if (!stored) {
      const legacy = JSON.parse(localStorage.getItem(LEGACY_COLUMNS_STORAGE_KEY) ?? 'null') as VisibilityState | null;
      if (legacy && typeof legacy === 'object') {
        const { createdAt: _hiddenByOldDefault, ...rest } = legacy;
        stored = rest;
      }
    }
    return stored && typeof stored === 'object' ? { ...DEFAULT_HIDDEN, ...stored } : DEFAULT_HIDDEN;
  } catch {
    return DEFAULT_HIDDEN;
  }
}

function useColumnVisibility() {
  const [visibility, setVisibility] = useState<VisibilityState>(loadColumnVisibility);
  const update = useCallback((next: VisibilityState) => {
    setVisibility(next);
    try {
      localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage can be unavailable (private mode); the choice then lasts for this page only.
    }
  }, []);
  return [visibility, update] as const;
}

/** Custom field columns start hidden; an explicit choice is remembered like the built-in ones. */
const isVisible = (visibility: VisibilityState, id: string) => (id in visibility ? visibility[id] !== false : !id.startsWith('cf:'));

function ColumnsMenu({ visibility, onChange }: { visibility: VisibilityState; onChange: (next: VisibilityState) => void }) {
  const { t } = useTranslation();
  const fields = useCustomFields().filter((f) => !f.archived);
  const labels: Record<(typeof OPTIONAL_COLUMNS)[number], string> = {
    phase: t('work.columns.phase'),
    priority: t('work.columns.priority'),
    type: t('work.columns.type'),
    points: t('work.columns.points'),
    assignee: t('work.columns.assignee'),
    owner: t('work.columns.owner'),
    scope: t('work.columns.project'),
    team: t('work.columns.team'),
    labels: t('work.columns.labels'),
    startDate: t('work.columns.start'),
    dueDate: t('work.columns.due'),
    delivery: t('work.columns.delivery'),
    createdAt: t('work.columns.created'),
    updatedAt: t('work.columns.updated'),
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="iconSm" variant="ghost" aria-label={t('work.columnsMenu.title')} title={t('work.columnsMenu.title')}>
          <TableProperties />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('work.columnsMenu.title')}</DropdownMenuLabel>
        {OPTIONAL_COLUMNS.map((id) => (
          <DropdownMenuItem key={id} onSelect={(e) => (e.preventDefault(), onChange({ ...visibility, [id]: !isVisible(visibility, id) }))}>
            <Checkbox checked={isVisible(visibility, id)} aria-hidden tabIndex={-1} /> {labels[id]}
          </DropdownMenuItem>
        ))}
        {fields.length ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('fields.title')}</DropdownMenuLabel>
            {fields.map((f) => (
              <DropdownMenuItem key={f.id} onSelect={(e) => (e.preventDefault(), onChange({ ...visibility, [`cf:${f.id}`]: !isVisible(visibility, `cf:${f.id}`) }))}>
                <Checkbox checked={isVisible(visibility, `cf:${f.id}`)} aria-hidden tabIndex={-1} /> <span className="truncate">{f.name}</span>
                {f.project ? <span className="ml-auto text-[10px] text-fg-subtle">{f.project.key}</span> : null}
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => onChange(DEFAULT_HIDDEN)}>{t('work.columnsMenu.reset')}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

type Section = { key: string; header: boolean; label: string; items: WorkItemListItem[] };

function GroupHeader({ section, collapsed, onToggle }: { section: Section; collapsed: boolean; onToggle: () => void }) {
  return (
    <button type="button" onClick={onToggle} aria-expanded={!collapsed} className="flex items-center gap-1.5 text-xs font-semibold text-fg-muted hover:text-fg">
      <ChevronRight className={cn('size-3.5 transition-transform', !collapsed && 'rotate-90')} aria-hidden />
      {section.label}
      <span className="font-normal text-fg-subtle">{section.items.length}</span>
    </button>
  );
}

function WorkTable({ items, selectedId, onOpen, columnVisibility, groupBy, params, setParams, editing }: { items: WorkItemListItem[]; selectedId: string | null; onOpen: (key: string) => void; columnVisibility: VisibilityState; groupBy: GroupField | null; params: URLSearchParams; setParams: (update: (p: URLSearchParams) => URLSearchParams) => void; editing: boolean }) {
  const { t } = useTranslation();
  const catalog = useLabelCatalog();
  const [selection, setSelection] = useState<RowSelectionState>({});
  // `selected` holds the display key; older links carry the UUID.
  const isSelected = (w: WorkItemListItem) => selectedId === w.key || selectedId === w.id;
  const allFields = useCustomFields();
  const fields = useMemo(() => allFields.filter((f) => !f.archived), [allFields]);
  const { move, dialog } = usePhaseMove();
  const versions = useRef(new Map<string, number>());
  // Rows refetched from the server carry the current version again.
  useEffect(() => {
    for (const item of items) if ((versions.current.get(item.id) ?? 0) <= item.version) versions.current.delete(item.id);
  }, [items]);
  // Stable handles keep the column definitions (and the editors in them) from being rebuilt on every render.
  const handlers = useRef({ move, onOpen });
  handlers.current = { move, onOpen };
  // Inline edits send one field at a time with the row's version; a stale row reports a conflict.
  const save = useCallback(
    async (item: WorkItemListItem, patch: Omit<UpdateWorkItem, 'expectedVersion'>) => {
      try {
        const result = await api.call('updateWorkItem', { params: { id: item.id }, idempotencyKey: newIdempotencyKey(), body: { ...patch, expectedVersion: versions.current.get(item.id) ?? item.version } as UpdateWorkItem });
        // The next edit of this row uses the new version even before the list refetches.
        versions.current.set(item.id, result.data.version);
        await queryClient.invalidateQueries({ queryKey: ['listWorkItems'] });
      } catch (error) {
        toastError(error);
        await queryClient.invalidateQueries({ queryKey: ['listWorkItems'] });
      }
    },
    [],
  );
  const effectiveVisibility = useMemo(() => Object.fromEntries([...Object.entries(columnVisibility), ...fields.map((f) => [`cf:${f.id}`, isVisible(columnVisibility, `cf:${f.id}`)] as const)]), [columnVisibility, fields]);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const columns = useMemo(
    () => [
      col.display({
        id: 'select',
        header: ({ table }) => <Checkbox checked={table.getIsAllRowsSelected() ? true : table.getIsSomeRowsSelected() ? 'indeterminate' : false} onCheckedChange={(v) => table.toggleAllRowsSelected(v === true)} aria-label="Select all" />,
        cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(v) => row.toggleSelected(v === true)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${row.original.key}`} />,
      }),
      col.accessor('key', { header: t('work.columns.key'), cell: (c) => <KeyLabel value={c.getValue()} /> }),
      col.accessor('title', {
        header: t('work.columns.title'),
        cell: (c) => (
          <div className="flex min-w-0 items-center gap-2">
            {c.row.original.blocked ? <AlertOctagon className="size-3.5 shrink-0 text-danger" aria-label={t('work.detail.blockedNote')} /> : null}
            <AgentCreatedIcon item={c.row.original} />
            {editing ? (
              <button type="button" className="truncate text-left font-medium text-fg hover:underline" onClick={(e) => (e.stopPropagation(), handlers.current.onOpen(c.row.original.key))}>
                {c.getValue()}
              </button>
            ) : (
              <span className="truncate font-medium text-fg">{c.getValue()}</span>
            )}
            {c.row.original.childProgress ? <ProgressPill progress={c.row.original.childProgress} /> : null}
            <NoEvidenceBadge basis={c.row.original.completionBasis} />
            {(columnVisibility.labels === false ? c.row.original.labels.slice(0, 2) : []).map((l) => (
              <LabelBadge key={l} label={l} catalog={catalog} className="hidden xl:inline-flex" />
            ))}
          </div>
        ),
      }),
      col.accessor('phase', {
        header: t('work.columns.phase'),
        cell: (c) => {
          const item = c.row.original;
          const targets = reachablePhases(item);
          if (!editing || item.archivedAt || !targets.length) return <PhaseBadge phase={c.getValue()} />;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" onClick={stop} className="inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" aria-label={`${t('table.phaseMoveHint')} ${item.key}`}>
                  <PhaseBadge phase={c.getValue()} /> <ChevronDown className="size-3 text-fg-subtle" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent onClick={stop}>
                <DropdownMenuLabel>{t('table.phaseMoveHint')}</DropdownMenuLabel>
                {targets.map((p) => (
                  <DropdownMenuItem key={p} onSelect={() => void handlers.current.move({ ...item, version: versions.current.get(item.id) ?? item.version }, p)}>
                    {t(`status.phase.${p}`)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      }),
      col.accessor('priority', {
        header: t('work.columns.priority'),
        cell: (c) =>
          editing && !c.row.original.archivedAt ? (
            <NativeSelect className="h-7 w-28" value={c.getValue()} onClick={stop} onChange={(e) => void save(c.row.original, { priority: e.target.value as UpdateWorkItem['priority'] })} aria-label={`${t('work.columns.priority')} ${c.row.original.key}`}>
              {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
                <option key={p} value={p}>
                  {t(`status.priority.${p}`)}
                </option>
              ))}
            </NativeSelect>
          ) : (
            <PriorityLabel priority={c.getValue()} />
          ),
      }),
      col.accessor('type', { header: t('work.columns.type'), cell: (c) => <span className="text-xs text-fg-muted">{t(`status.workType.${c.getValue()}`)}</span> }),
      // Not sortable: the list sorts in SQL by keyset, and points are a read-time recursive sum.
      col.display({ id: 'points', header: t('work.columns.points'), cell: ({ row }) => <PointsBadge weight={row.original.weight} compact /> }),
      col.accessor('assignee', {
        header: t('work.columns.assignee'),
        cell: (c) =>
          editing && !c.row.original.archivedAt ? (
            <div className="w-44" onClick={stop}>
              <PrincipalPicker label={`${t('work.columns.assignee')} ${c.row.original.key}`} value={c.getValue() ? { id: c.getValue()!.id, name: c.getValue()!.displayName } : null} onChange={(p) => void save(c.row.original, { assigneeId: p?.id ?? null })} />
            </div>
          ) : (
            <UserChip user={c.getValue()} link />
          ),
      }),
      col.accessor('owner', { header: t('work.columns.owner'), cell: (c) => <UserChip user={c.getValue()} link /> }),
      col.display({ id: 'scope', header: t('work.columns.project'), cell: ({ row }) => <span className="truncate text-xs text-fg-muted">{row.original.project?.name ?? row.original.team?.name ?? '—'}</span> }),
      col.display({ id: 'team', header: t('work.columns.team'), cell: ({ row }) => <span className="truncate text-xs text-fg-muted">{row.original.team?.name ?? '—'}</span> }),
      col.display({
        id: 'labels',
        header: t('work.columns.labels'),
        cell: ({ row }) => (
          <span className="flex gap-1">
            {row.original.labels.slice(0, 3).map((l) => (
              <LabelBadge key={l} label={l} catalog={catalog} />
            ))}
          </span>
        ),
      }),
      col.accessor('startDate', {
        header: t('work.columns.start'),
        cell: (c) =>
          editing && !c.row.original.archivedAt ? (
            <span onClick={stop}>
              <DateInput className="h-7 w-36" aria-label={`${t('work.columns.start')} ${c.row.original.key}`} value={c.getValue()} max={c.row.original.dueDate ?? undefined} onCommit={(startDate) => void save(c.row.original, { startDate })} />
            </span>
          ) : (
            <span className="text-xs text-fg-muted">{formatCalendarDate(c.getValue(), { month: 'short', day: 'numeric' })}</span>
          ),
      }),
      col.accessor('dueDate', {
        header: t('work.columns.due'),
        cell: (c) =>
          editing && !c.row.original.archivedAt ? (
            <span onClick={stop}>
              <DateInput className="h-7 w-36" aria-label={`${t('work.columns.due')} ${c.row.original.key}`} value={c.getValue()} min={c.row.original.startDate ?? undefined} onCommit={(dueDate) => void save(c.row.original, { dueDate })} />
            </span>
          ) : (
            <DueLabel date={c.getValue()} phase={c.row.original.phase} compact />
          ),
      }),
      col.display({ id: 'delivery', header: t('work.columns.delivery'), cell: ({ row }) => (row.original.completionPolicy === 'PRODUCTION' && row.original.phase !== 'TODO' ? <DeliveryBadge state={row.original.deliveryState} /> : null) }),
      col.accessor('createdAt', { header: t('work.columns.created'), cell: (c) => <span className="text-xs"><DateWithTime iso={c.getValue()} /></span> }),
      col.accessor('updatedAt', { header: t('work.columns.updated'), cell: (c) => <span className="text-xs"><TimeAgo iso={c.getValue()} /></span> }),
      ...fields.map((f) =>
        col.display({
          id: `cf:${f.id}`,
          header: f.name,
          cell: ({ row }) => {
            // Project fields only apply to that project's work.
            if (f.scope === 'PROJECT' && row.original.project?.id !== f.project?.id) return <span className="text-fg-subtle">—</span>;
            return editing && !row.original.archivedAt ? (
              <div className="w-40">
                <FieldEditor field={f} label={`${f.name} ${row.original.key}`} value={row.original.customFields[f.id]} onCommit={(value) => void save(row.original, { customFields: { [f.id]: value } })} />
              </div>
            ) : (
              <span className="text-xs">
                <FieldValue field={f} value={row.original.customFields[f.id]} />
              </span>
            );
          },
        }),
      ),
    ],
    [t, columnVisibility, catalog, editing, fields, save],
  );
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (r) => r.id,
    state: { rowSelection: selection, columnVisibility: effectiveVisibility },
    onRowSelectionChange: setSelection,
    enableRowSelection: true,
  });
  const selected = items.filter((i) => selection[i.id]);
  const rowById = new Map(table.getRowModel().rows.map((r) => [r.id, r]));
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const sections: Section[] = groupBy
    ? groupItems(items, groupBy).map((g) => ({ key: g.key || '__none', header: true, label: 'i18n' in g.label ? t(g.label.i18n) : g.label.text, items: g.items }))
    : [{ key: '__all', header: false, label: '', items }];
  return (
    <>
      {selected.length ? <BulkBar items={selected} onDone={() => setSelection({})} /> : null}
      {editing ? <p className="hidden border-b border-border bg-accent-soft/40 px-3 py-1.5 text-xs text-accent-soft-fg md:block">{t('table.editModeOn')}</p> : null}
      {dialog}
      <ul className="divide-y divide-border md:hidden">
        {sections.map((section) => (
          <Fragment key={section.key}>
            {section.header ? (
              <li className="bg-surface-2 px-4 py-1.5">
                <GroupHeader section={section} collapsed={collapsed.has(section.key)} onToggle={() => toggle(section.key)} />
              </li>
            ) : null}
            {collapsed.has(section.key)
              ? null
              : section.items.map((w) => (
                  <li key={`${section.key}:${w.id}`}>
                    <button type="button" data-testid="work-row" onClick={() => onOpen(w.key)} className="flex w-full flex-col gap-1.5 bg-surface px-4 py-3 text-left">
                      <span className="flex items-center gap-2">
                        <KeyLabel value={w.key} />
                        <AgentCreatedIcon item={w} />
                        {w.blocked ? <AlertOctagon className="size-3.5 text-danger" aria-label={t('work.detail.blockedNote')} /> : null}
                        <span className="ml-auto text-xs"><TimeAgo iso={w.updatedAt} /></span>
                      </span>
                      <span className="text-sm font-medium text-fg">{w.title}</span>
                      <span className="flex flex-wrap items-center gap-2">
                        <PhaseBadge phase={w.phase} />
                        <NoEvidenceBadge basis={w.completionBasis} />
                        <PriorityLabel priority={w.priority} />
                        <DueLabel date={w.dueDate} phase={w.phase} compact />
                        {w.assignee ? <UserChip user={w.assignee} /> : null}
                      </span>
                    </button>
                  </li>
                ))}
          </Fragment>
        ))}
      </ul>
      <table className="hidden w-full border-collapse text-sm md:table">
        <thead className="sticky top-0 z-10 bg-surface-2">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id} className="border-b border-border">
              {hg.headers.map((h) => (
                <th key={h.id} scope="col" className={cn('px-3 py-2 text-left text-xs font-medium text-fg-subtle', h.id === 'select' && 'w-8', ['delivery', 'scope'].includes(h.id) && 'hidden lg:table-cell', h.id === 'assignee' && 'hidden md:table-cell')}>
                  {SORTABLE[h.id] && typeof h.column.columnDef.header === 'string' ? <SortHeader label={h.column.columnDef.header} sortKey={SORTABLE[h.id]!} params={params} setParams={setParams} /> : flexRender(h.column.columnDef.header, h.getContext())}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {sections.map((section) => (
          <tbody key={section.key}>
            {section.header ? (
              <tr className="border-b border-border bg-surface-2">
                <td colSpan={table.getVisibleLeafColumns().length} className="px-3 py-1.5">
                  <GroupHeader section={section} collapsed={collapsed.has(section.key)} onToggle={() => toggle(section.key)} />
                </td>
              </tr>
            ) : null}
            {collapsed.has(section.key)
              ? null
              : section.items.map((item) => {
                  const row = rowById.get(item.id);
                  if (!row) return null;
                  return (
                    <tr
                      key={`${section.key}:${row.id}`}
                      data-testid="work-row"
                      tabIndex={0}
                      onClick={() => !editing && onOpen(row.original.key)}
                      onKeyDown={(e) => !editing && e.key === 'Enter' && onOpen(row.original.key)}
                      className={cn(!editing && 'cursor-pointer', 'border-b border-border bg-surface hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', isSelected(row.original) && 'bg-accent-soft/30')}
                      aria-selected={isSelected(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={cn('max-w-0 px-3 py-2', cell.column.id === 'title' ? 'w-full min-w-64' : 'whitespace-nowrap', ['delivery', 'scope'].includes(cell.column.id) && 'hidden lg:table-cell', cell.column.id === 'assignee' && 'hidden md:table-cell', cell.column.id !== 'title' && 'max-w-none')}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        ))}
      </table>
    </>
  );
}

/** Atomic bulk change (max 100); a hidden or conflicting item fails the whole command. */
function BulkBar({ items, onDone }: { items: WorkItemListItem[]; onDone: () => void }) {
  const { t } = useTranslation();
  const bulk = useApiMutation('bulkUpdateWork', { invalidate: ['listWorkItems', 'getWorkItem'] });
  const projects = useApiQuery('listProjects', { query: {} });
  const teams = useApiQuery('listTeams', { query: {} });
  const [label, setLabel] = useState('');
  const present = [...new Set(items.flatMap((i) => i.labels))].sort();
  const [canceling, setCanceling] = useState(false);
  const allArchived = items.every((i) => i.archivedAt);
  const allActive = items.every((i) => isActivePhase(i.phase) && !i.archivedAt);
  type Patch = { priority?: 'URGENT' | 'HIGH' | 'NORMAL' | 'LOW'; assigneeId?: string | null; labelsAdd?: string[]; labelsRemove?: string[]; projectId?: string | null; teamId?: string; archived?: boolean; dueDate?: string | null; cancel?: { reason: string } };
  const run = async (patch: Patch) => {
    try {
      await bulk.mutateAsync({ idempotencyKey: newIdempotencyKey(), body: { items: items.map((i) => ({ id: i.id, expectedVersion: i.version })), ...patch } });
      toast.success(t('common.changesSaved'));
      setLabel('');
      setCanceling(false);
      onDone();
    } catch (error) {
      toastError(error);
    }
  };
  const select = 'h-7 w-auto';
  return (
    <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-accent/40 bg-accent-soft px-3 py-2 text-sm text-accent-soft-fg">
      <span className="font-medium">{t('work.bulk.selected', { count: items.length })}</span>
      {allArchived ? null : (
        <>
          <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ priority: e.target.value as Patch['priority'] })} aria-label={t('work.bulk.setPriority')}>
            <option value="">{t('work.bulk.setPriority')}</option>
            {(['URGENT', 'HIGH', 'NORMAL', 'LOW'] as const).map((p) => (
              <option key={p} value={p}>
                {t(`status.priority.${p}`)}
              </option>
            ))}
          </NativeSelect>
          <div className="w-44">
            <PrincipalPicker label={t('work.bulk.assign')} value={null} placeholder={t('work.bulk.assign')} onChange={(p) => void run({ assigneeId: p?.id ?? null })} />
          </div>
          <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ projectId: e.target.value === 'none' ? null : e.target.value })} aria-label={t('work.bulk.setProject')}>
            <option value="">{t('work.bulk.setProject')}</option>
            <option value="none">{t('work.bulk.noProject')}</option>
            {(projects.data?.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.key} · {p.name}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ teamId: e.target.value })} aria-label={t('work.bulk.setTeam')}>
            <option value="">{t('work.bulk.setTeam')}</option>
            {(teams.data?.data ?? []).map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </NativeSelect>
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              if (label.trim()) void run({ labelsAdd: [label.trim()] });
            }}
          >
            <Input className="h-7 w-28" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={40} placeholder={t('work.bulk.labelPlaceholder')} aria-label={t('work.bulk.addLabel')} />
            <Button type="submit" size="sm" variant="secondary" disabled={!label.trim()}>
              <Plus /> {t('work.bulk.addLabel')}
            </Button>
          </form>
          <label className="flex items-center gap-1 text-xs">
            <span className="sr-only">{t('work.bulk.setDue')}</span>
            <DateInput className="h-7 w-36" title={t('work.bulk.setDue')} aria-label={t('work.bulk.setDue')} value={null} onCommit={(dueDate) => dueDate && void run({ dueDate })} />
          </label>
          {items.some((i) => i.dueDate) ? (
            <Button size="sm" variant="ghost" onClick={() => void run({ dueDate: null })}>
              {t('work.bulk.clearDue')}
            </Button>
          ) : null}
          {present.length ? (
            <NativeSelect className={select} value="" onChange={(e) => e.target.value && void run({ labelsRemove: [e.target.value] })} aria-label={t('work.bulk.removeLabel')}>
              <option value="">{t('work.bulk.removeLabel')}</option>
              {present.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </NativeSelect>
          ) : null}
        </>
      )}
      {allArchived ? (
        <Button size="sm" variant="secondary" loading={bulk.isPending} onClick={() => void run({ archived: false })}>
          <ArchiveRestore /> {t('work.bulk.restore')}
        </Button>
      ) : items.every((i) => isClosedPhase(i.phase)) ? (
        <Button size="sm" variant="secondary" loading={bulk.isPending} onClick={() => void run({ archived: true })}>
          <Archive /> {t('work.bulk.archive')}
        </Button>
      ) : null}
      {allActive ? (
        <Button size="sm" variant="secondary" onClick={() => setCanceling(true)}>
          <Ban /> {t('work.bulk.cancel')}
        </Button>
      ) : null}
      <Button size="sm" variant="ghost" onClick={onDone} className="ml-auto">
        {t('work.bulk.clear')}
      </Button>
      {canceling ? <BulkCancelDialog count={items.length} pending={bulk.isPending} onConfirm={(reason) => void run({ cancel: { reason } })} onClose={() => setCanceling(false)} /> : null}
    </div>
  );
}

/** One shared reason for every selected item, as the single-item cancel dialog asks for. */
function BulkCancelDialog({ count, pending, onConfirm, onClose }: { count: number; pending: boolean; onConfirm: (reason: string) => void; onClose: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent title={t('work.bulk.cancelTitle', { count })} description={t('work.bulk.cancelHint')}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reason.trim()) onConfirm(reason.trim());
          }}
          className="flex flex-col gap-4"
        >
          <Field label={t('common.reason')} required>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t('common.reasonPlaceholder')} autoFocus />
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="danger" loading={pending} disabled={!reason.trim()}>
              {t('work.bulk.cancel')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A per-browser on/off preference (for example editing in the table). */
function useStoredFlag(key: string) {
  const [value, setValue] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const update = useCallback(
    (next: boolean) => {
      setValue(next);
      try {
        localStorage.setItem(key, next ? '1' : '0');
      } catch {
        // Storage can be unavailable; the choice then lasts for this page only.
      }
    },
    [key],
  );
  return [value, update] as const;
}
